import {
  type BetaSignupInput,
  BetaSignupInputSchema,
  type BetaSignupResponse,
  BetaSignupResponseSchema,
} from '@sazeo/schemas';
import { useMutation } from '@tanstack/react-query';

export class SignupApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = 'SignupApiError';
    this.code = code;
    this.status = status;
  }
}

type ErrorEnvelope = {
  error?: {
    code?: string;
    message?: string;
  };
};

async function readError(res: Response): Promise<SignupApiError> {
  const body = (await res.json().catch(() => null)) as ErrorEnvelope | null;
  return new SignupApiError(
    body?.error?.code ?? 'signup_failed',
    body?.error?.message ?? res.statusText,
    res.status,
  );
}

/**
 * Read VITE_SUPABASE_URL via the literal-access pattern Vite needs (any
 * aliasing breaks static replacement). Fallback to globalThis injection
 * (used by tenant-app) for the same reason.
 */
function supabaseBase(): string {
  const fromVite = import.meta.env?.VITE_SUPABASE_URL;
  if (fromVite) return fromVite;
  const fromGlobal = (
    globalThis as unknown as { __SAZEO_VITE_ENV__?: { VITE_SUPABASE_URL?: string } }
  ).__SAZEO_VITE_ENV__?.VITE_SUPABASE_URL;
  return fromGlobal ?? '';
}

function supabaseAnon(): string {
  const fromVite = import.meta.env?.VITE_SUPABASE_ANON_KEY;
  if (fromVite) return fromVite;
  const fromGlobal = (
    globalThis as unknown as { __SAZEO_VITE_ENV__?: { VITE_SUPABASE_ANON_KEY?: string } }
  ).__SAZEO_VITE_ENV__?.VITE_SUPABASE_ANON_KEY;
  return fromGlobal ?? '';
}

export async function signup(input: BetaSignupInput): Promise<BetaSignupResponse> {
  const payload = BetaSignupInputSchema.parse(input);
  // Call signup-admin on the absolute Supabase URL. Intranet domain
  // doesn't proxy /functions/v1 — relative URL would 404. The signup-admin
  // accepts anonymous SDK calls (rate-limited per-IP).
  const base = supabaseBase().replace(/\/+$/, '');
  if (!base) throw new SignupApiError('config_error', 'VITE_SUPABASE_URL not configured', 500);
  const res = await fetch(`${base}/functions/v1/signup-admin`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      apikey: supabaseAnon(),
      authorization: `Bearer ${supabaseAnon()}`,
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    throw await readError(res);
  }

  const body = await res.json();
  return BetaSignupResponseSchema.parse(body);
}

export function useSignup() {
  return useMutation({
    mutationFn: signup,
  });
}
